import * as React from 'react';

import {FormField} from 'shells/form/form-field';
import {Radio} from 'shells/radio/radio';
import {RadioGroupSlider} from 'shells/radio/radio-group-slider';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {SelectPickerGenericCreatable} from 'shells/select-picker-generic/select-picker-generic-creatable';
import type {Options} from 'shells/select-picker-generic/types';
import {CharacterLimitedTextfield} from 'shells/textfield/character-limited-textfield';
import {TextField} from 'shells/textfield/textfield';

import type {ProductType} from 'nutshell-graphql-types';
import type {
    QuoteTerm as ProductTerm,
    GetProductsCategories_productCategories as ProductCategory,
} from 'nutshell-graphql-types';

import {useCreateProductCategory} from '../../../categories/graphql/hooks/use-create-product-category';
import {useGetProductCategories} from '../../../categories/graphql/hooks/use-get-product-categories';

import {PRODUCT_TYPE_SELECT_OPTIONS, UNIT_SELECT_OPTIONS} from './consts';

export const MAX_DESCRIPTION_LENGTH = 10000;

const UNCATEGORIZED_PRODUCT_CATEGORY_OPTION = {
    value: 'uncategorized',
    label: 'Uncategorized',
} as const;

type Props = {
    productName: string;
    setProductName: (value: string) => void;
    productType: ProductType;
    setProductType: (value: ProductType) => void;
    productCategory: ProductCategory | null | undefined;
    setProductCategory: (value?: ProductCategory | null | undefined) => void;
    productDescription: string;
    setProductDescription: (value: string) => void;
    productUnit: ProductTerm | null;
    setProductUnit: (value: ProductTerm | null) => void;
    productSku: string;
    setProductSku: (value: string) => void;
    isCreatingCategory: boolean;
};

export function ProductInputFields(props: Props) {
    const {productCategories} = useGetProductCategories();
    const {createProductCategory, isLoading: isCreatingProductCategory} =
        useCreateProductCategory();

    const productCategoryOptions: Options = [
        UNCATEGORIZED_PRODUCT_CATEGORY_OPTION,
        ...productCategories.map((option) =>
            option ? {value: option.id, label: option.name} : {value: '', label: 'Unknown'}
        ),
    ];

    return (
        <>
            <FormField innerId='name' label='Name' isRequired={true}>
                <TextField
                    id='name'
                    // @ts-expect-error
                    name='name'
                    placeholder='Name'
                    focusOnMount={true}
                    value={props.productName}
                    onChange={(value) => props.setProductName(value)}
                    ignorePasswordAutoFill={true}
                />
            </FormField>
            <FormField innerId='type' label='Product type'>
                <RadioGroupSlider
                    name='entity'
                    size='big'
                    selectedIndex={PRODUCT_TYPE_SELECT_OPTIONS.findIndex(
                        (option) => option.value === props.productType
                    )}
                    selectedValue={props.productType}
                    // @ts-expect-error
                    onChange={(value) => props.setProductType(value)}
                >
                    {PRODUCT_TYPE_SELECT_OPTIONS.map(
                        (option: {value: ProductType; label: string}) => {
                            return (
                                // @ts-expect-error
                                <Radio alignCenter={true} value={option.value} key={option.value}>
                                    {option.label}
                                </Radio>
                            );
                        }
                    )}
                </RadioGroupSlider>
            </FormField>
            <FormField innerId='category' label='Category'>
                <SelectPickerGenericCreatable
                    placeholder='Choose or create a category…'
                    options={productCategoryOptions}
                    value={productCategoryOptions.find((option) => {
                        if (props.productCategory) {
                            return option.value === props.productCategory.id;
                        }

                        return props.isCreatingCategory
                            ? null
                            : UNCATEGORIZED_PRODUCT_CATEGORY_OPTION;
                    })}
                    // @ts-expect-error
                    onChange={(option) => {
                        if (option.value === 'uncategorized') {
                            props.setProductCategory(null);
                        } else {
                            props.setProductCategory(
                                productCategories.find(
                                    (category) => category && category.id === option.value
                                )
                            );
                        }
                    }}
                    // @ts-expect-error
                    onCreateOption={(option) => {
                        createProductCategory({name: option}).then((productCategory) => {
                            props.setProductCategory(productCategory);
                        });
                    }}
                    isLoading={isCreatingProductCategory}
                    // @ts-expect-error
                    formatCreateLabel={(label) => `+ Create "${label}"`}
                />
            </FormField>
            <FormField innerId='sku' label='SKU'>
                <TextField
                    id='sku'
                    // @ts-expect-error
                    name='sku'
                    placeholder='SKU'
                    value={props.productSku}
                    onChange={(value) => props.setProductSku(value)}
                />
            </FormField>
            {props.productType === 'SERVICE' && (
                <FormField innerId='unit' label='Unit' isRequired={true}>
                    <SelectPickerGeneric
                        placeholder='Choose a unit…'
                        options={UNIT_SELECT_OPTIONS}
                        value={UNIT_SELECT_OPTIONS.find(
                            (option) => option.value === props.productUnit
                        )}
                        // @ts-expect-error
                        onChange={(option) => props.setProductUnit(option.value)}
                    />
                </FormField>
            )}
            <FormField innerId='description' label='Description'>
                <CharacterLimitedTextfield
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    placeholder='Description'
                    value={props.productDescription}
                    onChange={props.setProductDescription}
                    isMultiLine={true}
                />
            </FormField>
        </>
    );
}
